/**
 * Realm Roles and Statuses
 *
 * Defines role-based access control for multi-realm architecture.
 * Each user can have different roles across different realms within a tenant.
 * Issue #313 - Centralize enums to shared-types
 */

import { z } from 'zod';

// ============================================================================
// REALM STATUS
// ============================================================================

/**
 * Realm lifecycle status
 */
export enum RealmStatus {
  PROVISIONING = 'provisioning',
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  DELETED = 'deleted',
}

/** Zod schema for RealmStatus validation */
export const RealmStatusSchema = z.nativeEnum(RealmStatus);

/** Array of all realm statuses for iteration */
export const REALM_STATUSES = Object.values(RealmStatus);

// ============================================================================
// REALM ROLE
// ============================================================================

/**
 * Realm role types for multi-realm access control
 *
 * Roles are scoped to a specific realm:
 * - OWNER: Full control over the realm, can manage members and settings
 * - ADMIN: Can manage realm data and some settings
 * - MEMBER: Can access and modify data within the realm
 * - VIEWER: Read-only access to realm data
 */
export enum RealmRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member',
  VIEWER = 'viewer',
}

/** Zod schema for RealmRole validation */
export const RealmRoleSchema = z.nativeEnum(RealmRole);

/** Array of all realm roles for iteration */
export const REALM_ROLES = Object.values(RealmRole);

// ============================================================================
// REALM MEMBERSHIP STATUS
// ============================================================================

/**
 * Realm membership status
 *
 * Tracks the lifecycle of a user's membership in a realm:
 * - INVITED: User has been invited but hasn't accepted yet
 * - ACTIVE: User is an active member of the realm
 * - SUSPENDED: User's access has been temporarily suspended
 * - REMOVED: User has been removed from the realm
 */
export enum RealmMembershipStatus {
  INVITED = 'invited',
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  REMOVED = 'removed',
}

/** Zod schema for RealmMembershipStatus validation */
export const RealmMembershipStatusSchema = z.nativeEnum(RealmMembershipStatus);

/** Array of all realm membership statuses for iteration */
export const REALM_MEMBERSHIP_STATUSES = Object.values(RealmMembershipStatus);

// ============================================================================
// PROVISIONING STATUS
// ============================================================================

/**
 * Provisioning status for realm setup
 */
export enum ProvisioningStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

/** Zod schema for ProvisioningStatus validation */
export const ProvisioningStatusSchema = z.nativeEnum(ProvisioningStatus);

/** Array of all provisioning statuses for iteration */
export const PROVISIONING_STATUSES = Object.values(ProvisioningStatus);
